import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from './ui/sheet';
import {
  Plus, Loader2, Trash2, Pencil, MapPin, LayoutTemplate, Save, CheckSquare, Square,
  Copy, ArrowRight, Sparkles, Printer,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

const CATEGORY_OPTIONS = [
  { value: 'ashara', label: 'Ashara Mubaraka', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'ramadan', label: 'Ramadan', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'other', label: 'Other', color: 'bg-slate-100 text-slate-700 border-slate-300' },
];
const catMeta = (c) => CATEGORY_OPTIONS.find(o => o.value === (c || 'other')) || CATEGORY_OPTIONS[2];

// ============== TEMPLATE DESIGNER (Modal Sheet) ==============
function TemplateDesigner({ template, onClose, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState({
    name: template?.name || '',
    category: template?.category || 'ashara',
    rows: template?.rows || 13,
    cols: template?.cols || 30,
    hof_seat_limit: template?.hof_seat_limit ?? 0,
    min_age: template?.min_age ?? '',
    no_child_under: template?.no_child_under ?? '',
    is_first_floor_like: !!template?.is_first_floor_like,
  });
  const [selected, setSelected] = useState(new Set(
    (template?.seats || []).map(s => `${s.row},${s.col}`)
  ));
  const [disabled, setDisabled] = useState(new Set(
    (template?.disabled_cells || []).map(c => `${c.row},${c.col}`)
  ));
  const [mode, setMode] = useState('seat');
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null);

  const rows = parseInt(form.rows, 10) || 1;
  const cols = parseInt(form.cols, 10) || 1;

  const seatNumberMap = React.useMemo(() => {
    const sortedCells = Array.from(selected).map(k => {
      const [r, c] = k.split(',').map(Number);
      return { row: r, col: c };
    }).sort((a, b) => a.row - b.row || a.col - b.col);
    const m = {};
    sortedCells.forEach((c, i) => { m[`${c.row},${c.col}`] = i + 1; });
    return m;
  }, [selected]);

  const toggleCell = (r, c, forceMode) => {
    const key = `${r},${c}`;
    if (mode === 'seat') {
      setSelected(prev => {
        const next = new Set(prev);
        const has = next.has(key);
        const m = forceMode ?? (has ? 'remove' : 'add');
        if (m === 'add') {
          if (disabled.has(key)) setDisabled(prev2 => { const n2 = new Set(prev2); n2.delete(key); return n2; });
          next.add(key);
        } else { next.delete(key); }
        return next;
      });
    } else {
      setDisabled(prev => {
        const next = new Set(prev);
        const has = next.has(key);
        const m = forceMode ?? (has ? 'remove' : 'add');
        if (m === 'add') {
          if (selected.has(key)) setSelected(prev2 => { const n2 = new Set(prev2); n2.delete(key); return n2; });
          next.add(key);
        } else { next.delete(key); }
        return next;
      });
    }
  };

  const handleMouseDown = (r, c) => {
    const key = `${r},${c}`;
    const isSelected = mode === 'seat' ? selected.has(key) : disabled.has(key);
    const m = isSelected ? 'remove' : 'add';
    setDragMode(m); setIsDragging(true); toggleCell(r, c, m);
  };
  const handleMouseEnter = (r, c) => { if (isDragging && dragMode) toggleCell(r, c, dragMode); };
  const handleMouseUp = () => { setIsDragging(false); setDragMode(null); };

  const fillAll = () => {
    const all = new Set();
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      if (!disabled.has(`${r},${c}`)) all.add(`${r},${c}`);
    }
    setSelected(all);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      const seats = Array.from(selected).map(k => { const [r, c] = k.split(',').map(Number); return { row: r, col: c }; });
      const disabled_cells = Array.from(disabled).map(k => { const [r, c] = k.split(',').map(Number); return { row: r, col: c }; });
      const payload = {
        name: form.name.trim(),
        category: form.category,
        rows, cols,
        hof_seat_limit: parseInt(form.hof_seat_limit, 10) || 0,
        min_age: form.min_age === '' ? null : parseInt(form.min_age, 10),
        no_child_under: form.no_child_under === '' ? null : parseInt(form.no_child_under, 10),
        is_first_floor_like: !!form.is_first_floor_like,
        seats,
        disabled_cells,
      };
      if (template?.id) {
        await api.put(`/seat/templates/${template.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/seat/templates', payload);
        toast.success('Template created');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto p-0" data-testid="template-designer-sheet">
        <SheetHeader className="p-4 border-b sticky top-0 bg-white z-10">
          <SheetTitle className="flex items-center gap-2">
            <LayoutTemplate className="w-5 h-5 text-indigo-600" />
            {template?.id ? `Edit Template — ${template.name}` : 'New Floor Template'}
          </SheetTitle>
          <SheetDescription>
            Templates are reusable per category (Ashara / Ramadan). Once saved, you can apply this template to any event to instantly create a fully-designed floor.
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 space-y-4" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Category *</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm" data-testid="tpl-category">
                {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-3">
              <Label className="text-xs">Template Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ladies First Floor" data-testid="tpl-name" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <div>
              <Label className="text-xs">Rows</Label>
              <Input type="number" min={1} max={100} value={form.rows} onChange={(e) => setForm({ ...form, rows: e.target.value })} data-testid="tpl-rows" />
            </div>
            <div>
              <Label className="text-xs">Cols</Label>
              <Input type="number" min={1} max={100} value={form.cols} onChange={(e) => setForm({ ...form, cols: e.target.value })} data-testid="tpl-cols" />
            </div>
            <div>
              <Label className="text-xs">Seat/HOF (0 ∞)</Label>
              <Input type="number" min={0} value={form.hof_seat_limit} onChange={(e) => setForm({ ...form, hof_seat_limit: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Min Age</Label>
              <Input type="number" min={0} value={form.min_age} onChange={(e) => setForm({ ...form, min_age: e.target.value })} placeholder="40" />
            </div>
            <div>
              <Label className="text-xs">No child &lt;</Label>
              <Input type="number" min={0} value={form.no_child_under} onChange={(e) => setForm({ ...form, no_child_under: e.target.value })} placeholder="10" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_first_floor_like} onChange={(e) => setForm({ ...form, is_first_floor_like: e.target.checked })} />
            Treat as First-Floor (strict child rule)
          </label>

          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <div className="inline-flex rounded-md border bg-white">
              <button onClick={() => setMode('seat')}
                className={`px-3 py-1 text-xs ${mode === 'seat' ? 'bg-emerald-600 text-white' : 'text-slate-700'}`}>Seat</button>
              <button onClick={() => setMode('disable')}
                className={`px-3 py-1 text-xs ${mode === 'disable' ? 'bg-slate-700 text-white' : 'text-slate-700'}`}>Disable</button>
            </div>
            <Badge className="bg-emerald-600">{selected.size} seats</Badge>
            <Badge variant="outline">{disabled.size} disabled</Badge>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={fillAll}><CheckSquare className="w-3 h-3 mr-1" /> Fill all</Button>
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}><Square className="w-3 h-3 mr-1" /> Clear</Button>
          </div>

          <div className="overflow-auto p-2 bg-slate-50 rounded border max-h-[55vh]">
            <div className="inline-block">
              <div className="text-center text-[10px] font-semibold text-slate-500 mb-2 select-none">← QIBLA →</div>
              <div className="grid gap-0.5 select-none" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {Array.from({ length: rows }).map((_, r) =>
                  Array.from({ length: cols }).map((__, c) => {
                    const key = `${r},${c}`;
                    const isSel = selected.has(key);
                    const isDis = disabled.has(key);
                    const num = seatNumberMap[key];
                    let bg = 'bg-white hover:bg-emerald-50 border-slate-200';
                    if (isSel) bg = 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-700';
                    else if (isDis) bg = 'bg-slate-400 border-slate-500 text-white';
                    return (
                      <button key={key}
                        onMouseDown={() => handleMouseDown(r, c)}
                        onMouseEnter={() => handleMouseEnter(r, c)}
                        type="button"
                        className={`border rounded text-[9px] font-bold leading-none transition-colors ${bg}`}
                        style={{ width: 22, height: 22 }}
                        title={`Row ${r}, Col ${c}${num ? ` · #${num}` : ''}`}>
                        {isSel ? num : ''}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="p-4 border-t sticky bottom-0 bg-white gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="tpl-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Template
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ============== APPLY TEMPLATE DIALOG ==============
function ApplyTemplateDialog({ template, open, onClose, onDone }) {
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get('/seat/events');
        const all = res.data?.events || [];
        const same = all.filter(e => (e.category || 'other') === template.category);
        setEvents(same);
        setPicked(new Set());
      } catch { toast.error('Failed to load events'); }
    })();
    // eslint-disable-next-line
  }, [open, template?.id]);

  const toggle = (id) => setPicked(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const apply = async () => {
    if (picked.size === 0) { toast.error('Pick at least one event'); return; }
    setBusy(true);
    try {
      const res = await api.post(`/seat/templates/${template.id}/apply`, {
        event_ids: Array.from(picked),
        overwrite_existing: overwrite,
      });
      const { created, skipped } = res.data || {};
      toast.success(`Applied to ${created?.length || 0} event(s)${skipped?.length ? ` · ${skipped.length} skipped` : ''}`);
      if (skipped?.length) {
        skipped.forEach(s => toast.warning(`${s.event_name || s.event_id}: ${s.reason}`));
      }
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="apply-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="w-5 h-5 text-indigo-600" /> Apply Template</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-xs space-y-1">
            <div><strong>Template:</strong> {template?.name}</div>
            <div><strong>Category:</strong> <Badge variant="outline" className="text-[10px]">{catMeta(template?.category).label}</Badge></div>
            <div><strong>{template?.seat_count || (template?.seats?.length ?? 0)}</strong> seats · grid {template?.rows} × {template?.cols}</div>
            <div className="text-indigo-700">This will create a new floor (named after the template) in each selected event below.</div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Apply to which events?</Label>
            {events.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-6">No events in this category yet.</div>
            ) : (
              <div className="border rounded max-h-60 overflow-auto mt-1.5">
                {events.map(e => (
                  <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer text-sm">
                    <input type="checkbox" checked={picked.has(e.id)} onChange={() => toggle(e.id)} data-testid={`apply-evt-${e.id}`} />
                    <span className="font-medium flex-1 truncate">{e.name}</span>
                    {e.year && <Badge variant="outline" className="text-[10px] font-mono">{e.year}</Badge>}
                    <Badge variant="outline" className="text-[10px]">{e.floor_count} floor(s)</Badge>
                  </label>
                ))}
              </div>
            )}
          </div>
          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="apply-overwrite" />
            <span><strong>Overwrite existing floor with same name</strong> (only if no bookings exist on it)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={busy || picked.size === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="apply-template-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />}
            Apply to {picked.size} event{picked.size === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== ROOT PANEL ==============
export default function SeatTemplatesPanel() {
  const api = useApi();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [designing, setDesigning] = useState(null); // template object or { new: true }
  const [applying, setApplying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/seat/templates');
      setTemplates(res.data?.templates || []);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Existing floors built from this template will not be affected.`)) return;
    try {
      await api.delete(`/seat/templates/${t.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  // Print template as A4 landscape PDF in a new tab
  const printTemplate = async (t) => {
    try {
      const res = await api.get(`/seat/templates/${t.id}/print`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      // Auto-trigger print dialog once the PDF is loaded
      if (w) {
        w.addEventListener('load', () => { try { w.focus(); w.print(); } catch (_) { /* noop */ } });
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Print failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <LayoutTemplate className="w-5 h-5 text-indigo-600" /> Floor Templates
          </h2>
          <p className="text-xs text-slate-500">
            Build a floor layout <em>once</em> per category, then apply it to every Ashara / Ramadan event in subsequent years.
          </p>
        </div>
        <Button onClick={() => setDesigning({ new: true, category: 'ashara' })} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : templates.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          <Sparkles className="w-8 h-8 mx-auto mb-2 text-indigo-400" />
          No templates yet. Create one for <strong>Ashara</strong> or <strong>Ramadan</strong> — you'll reuse it across years.
        </CardContent></Card>
      ) : (
        <div className="space-y-6">
          {CATEGORY_OPTIONS.map(catOpt => {
            const grp = templates.filter(t => (t.category || 'other') === catOpt.value);
            if (grp.length === 0) return null;
            return (
              <div key={catOpt.value}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${catOpt.color}`}>{catOpt.label}</span>
                  <span className="text-xs text-slate-500">{grp.length} template{grp.length === 1 ? '' : 's'}</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {grp.map(t => (
                    <Card key={t.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${t.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-indigo-600" /> {t.name}
                            </h3>
                            <div className="text-xs text-slate-500 mt-0.5">{t.rows} × {t.cols} grid</div>
                          </div>
                          {t.is_first_floor_like && <Badge variant="outline" className="text-[10px]">1st Floor</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-1.5 text-xs">
                          <Badge variant="secondary">{t.seat_count} seats</Badge>
                          <Badge variant="outline">limit: {t.hof_seat_limit || '∞'}/HOF</Badge>
                          {t.min_age != null && <Badge variant="outline">age ≥ {t.min_age}</Badge>}
                          {t.no_child_under != null && <Badge variant="outline">no child &lt; {t.no_child_under}</Badge>}
                        </div>
                        <div className="flex gap-1 pt-1">
                          <Button size="sm" className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => setApplying(t)} data-testid={`apply-${t.id}`}>
                            <Copy className="w-3 h-3 mr-1" /> Apply
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => printTemplate(t)} data-testid={`print-tpl-${t.id}`} title="Print A4 landscape">
                            <Printer className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setDesigning(t)} data-testid={`edit-tpl-${t.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(t)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {designing && (
        <TemplateDesigner
          template={designing.new ? null : designing}
          onClose={() => setDesigning(null)}
          onSaved={() => { setDesigning(null); load(); }}
        />
      )}
      {applying && (
        <ApplyTemplateDialog
          template={applying}
          open={!!applying}
          onClose={() => setApplying(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
